import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { useData } from "@/contexts/DataContext";
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Home,
  Package,
  IndianRupee,
  Settings,
  BarChart3,
  Bell,
  ScanBarcode,
  Sparkles,
  Crown,
  Shield,
  Smartphone,
  Moon,
  Star,
  Mail,
  HelpCircle,
  Bluetooth,
  Printer,
  Gift,
  Users,
  Activity,
  Monitor,
  ShoppingBag,
  Receipt,
} from "lucide-react";

type SectionId = string;

function GuideSection({
  id,
  icon: Icon,
  title,
  openSection,
  toggle,
  children,
}: {
  id: SectionId;
  icon: any;
  title: string;
  openSection: SectionId | null;
  toggle: (id: SectionId) => void;
  children: React.ReactNode;
}) {
  const isOpen = openSection === id;
  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
      <button
        onClick={() => toggle(id)}
        className="w-full flex items-center gap-3 p-4 active:bg-slate-50 dark:active:bg-slate-800 transition-colors"
        data-testid={`guide-section-${id}`}
      >
        <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
        </div>
        <span className="flex-1 text-left font-semibold text-base text-slate-900 dark:text-white">
          {title}
        </span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-5 pt-1 text-sm text-slate-700 dark:text-slate-300 space-y-3 border-t border-slate-100 dark:border-slate-800">
          {children}
        </div>
      )}
    </div>
  );
}

function BulletItem({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex gap-2">
      <span className="text-[#8B2121] dark:text-red-400 font-bold mt-0.5">
        •
      </span>
      <p>
        <strong className="text-slate-900 dark:text-white">{title}</strong>{" "}
        {desc}
      </p>
    </div>
  );
}

function PointsTable({ rows }: { rows: [string, string][] }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            <th className="text-left px-3 py-2 font-semibold text-slate-900 dark:text-white">
              Activity
            </th>
            <th className="text-right px-3 py-2 font-semibold text-slate-900 dark:text-white">
              Points
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([activity, points], i) => (
            <tr
              key={i}
              className={
                i % 2 === 0
                  ? "bg-white dark:bg-slate-900"
                  : "bg-slate-50/50 dark:bg-slate-800/50"
              }
            >
              <td className="px-3 py-2">{activity}</td>
              <td className="px-3 py-2 text-right font-semibold text-amber-600 dark:text-amber-400">
                {points}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlansTable({ rows }: { rows: [string, string, string][] }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            <th className="text-left px-3 py-2 font-semibold text-slate-900 dark:text-white">
              Plan
            </th>
            <th className="text-center px-3 py-2 font-semibold text-slate-900 dark:text-white">
              Points
            </th>
            <th className="text-right px-3 py-2 font-semibold text-slate-900 dark:text-white">
              Duration
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([plan, points, duration], i) => (
            <tr
              key={i}
              className={
                i % 2 === 0
                  ? "bg-white dark:bg-slate-900"
                  : "bg-slate-50/50 dark:bg-slate-800/50"
              }
            >
              <td className="px-3 py-2">{plan}</td>
              <td className="px-3 py-2 text-center font-semibold text-amber-600 dark:text-amber-400">
                {points}
              </td>
              <td className="px-3 py-2 text-right">{duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function UserGuide() {
  const { isUltimateOwnerMobile } = useData();
  const [activeTab, setActiveTab] = useState<"normal" | "pro" | "ultimate">(
    isUltimateOwnerMobile ? "ultimate" : "normal"
  );
  const [openSection, setOpenSection] = useState<SectionId | null>(null);

  useEffect(() => {
    if (isUltimateOwnerMobile && activeTab !== "ultimate") {
      setActiveTab("ultimate");
      setOpenSection(null);
    }
  }, [isUltimateOwnerMobile, activeTab]);

  const toggle = (id: SectionId) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Logo size="md" />
          </div>
        </div>
      </header>

      <div className="flex-1 px-4 py-4 space-y-4 pb-24 overflow-y-auto">
        <div className="text-center mb-2">
          <div className="flex items-center justify-center gap-2 mb-1">
            <BookOpen className="h-6 w-6 text-[#8B2121] dark:text-red-400" />
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              User Guide
            </h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Tap a section to learn more
          </p>
        </div>

        {!isUltimateOwnerMobile && (
          <div className="flex gap-2 p-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => {
                setActiveTab("normal");
                setOpenSection(null);
              }}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === "normal"
                ? "bg-[#8B2121] text-white shadow-sm"
                : "text-slate-500 dark:text-slate-400"
                }`}
              data-testid="guide-tab-normal"
            >
              Normal Mode
            </button>
            <button
              onClick={() => {
                setActiveTab("pro");
                setOpenSection(null);
              }}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === "pro"
                ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm"
                : "text-slate-500 dark:text-slate-400"
                }`}
              data-testid="guide-tab-pro"
            >
              <Crown className="h-4 w-4" />
              Pro Mode
            </button>
          </div>
        )}

        {activeTab === "normal" && (
          <div className="space-y-3">
            <GuideSection
              id="n-start"
              icon={Sparkles}
              title="Getting Started"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Sign In:"
                desc="Open DailyHisaab app and login with WhatsApp OTP. No separate password needed."
              />
              <BulletItem
                title="Pick Your Shop Type:"
                desc="On first sign-in, choose what kind of shop you run (Kirana, Restaurant, Pharmacy, Cloth, Hardware, Mobile, Wholesale, or Other). We'll auto-add a few sample products and set the right GST rate so you can start billing immediately. Edit or delete the samples anytime."
              />
              <BulletItem
                title="First Bill Preview:"
                desc="Right after setup, you'll see exactly what your printed receipt will look like — shop name on top, items in the middle, NET PAYABLE at the bottom. Confirm and you're on the dashboard."
              />
              <BulletItem
                title="Your Data:"
                desc="All your products and transactions are stored on our secure cloud and synced across your devices."
              />
              <BulletItem
                title="Try Pro Free:"
                desc="Every new account gets a one-time 7-day free Pro trial — unlock GST invoicing, stock tracking, cost price, low-stock alerts and more. Activate from Settings → Billing → Start 7-Day Free Trial."
              />
              <BulletItem
                title="Get the App:"
                desc="Download DailyHisaab from the Google Play Store or your phone's app store (Xiaomi, Samsung, Vivo, Indus). Always update to the latest version for new features."
              />
            </GuideSection>

            <GuideSection
              id="n-home"
              icon={Home}
              title="Dashboard & Billing"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Product Cards:"
                desc="All your products appear as tappable cards on the Dashboard."
              />
              <BulletItem
                title="Quick Billing:"
                desc="Tap a product to add it to your cart. A billing bar appears at the bottom showing the running total."
              />
              <BulletItem
                title="Record Sale:"
                desc="Tap 'Sell' to record a sale transaction."
              />
              <BulletItem
                title="Record Expense:"
                desc="Tap 'Buy' to record an expense or purchase."
              />
              <BulletItem
                title="Favourites:"
                desc="Tap the heart icon on any product to pin it to the top for quick access."
              />
              <BulletItem
                title="Discount:"
                desc="Apply a bill-level discount before recording a sale."
              />
            </GuideSection>

            <GuideSection
              id="n-inventory"
              icon={Package}
              title="Products"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Add Products:"
                desc="Tap '+ Add Product' and fill in the product name, sale price, and category."
              />
              <BulletItem
                title="Barcode Scanning:"
                desc="Tap 'Scan Barcode' to use your camera. DailyHisaab searches online databases to auto-fill the product name."
              />
              <BulletItem
                title="Edit Products:"
                desc="Tap any product to edit its details — name, price, category."
              />
              <BulletItem
                title="Delete Products:"
                desc="Swipe or tap delete on any product to remove it."
              />
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-950/30 rounded-xl px-3 py-2">
                You earn +1 points for each product added manually and +5
                points for products added via barcode scan.
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Bluetooth thermal receipt + barcode label printing is a Pro
                feature — upgrade to print receipts and product labels.
              </p>
            </GuideSection>

            <GuideSection
              id="n-transactions"
              icon={IndianRupee}
              title="Transactions"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="View All:"
                desc="See all your recorded sales and expenses listed with date, items, and total."
              />
              <BulletItem
                title="Today's Sales:"
                desc="Tap the 'Today's Sales' card at the top to instantly see all of today's sales in a quick-view sheet."
              />
              <BulletItem
                title="Filter by Type:"
                desc="Switch between All, Sales only, or Expenses only using the dropdown."
              />
              <BulletItem
                title="Filter by Date:"
                desc="Filter by Today, This Week, This Month, This Year, or All Time."
              />
              <BulletItem
                title="Share Bill:"
                desc="Tap 'Share bill' on any sale to generate and share the invoice — via WhatsApp, link, or PDF download."
              />
              <BulletItem
                title="Bill Number:"
                desc="Each transaction shows a short 8-character bill ID for easy reference."
              />
              <BulletItem
                title="Delete:"
                desc="Remove any transaction using the trash icon."
              />
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-950/30 rounded-xl px-3 py-2">
                You earn +1 point for each sale and +2 points for each record & share
                action.
              </p>
            </GuideSection>

            <GuideSection
              id="n-settings"
              icon={Settings}
              title="Settings & Profile"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Business Profile:"
                desc="Add your owner name, business name, address, phone, email, and website. These appear on shared invoices."
              />
              <BulletItem
                title="Business Logo:"
                desc="Tap your logo avatar to upload, change, or remove your business logo. Use a black-and-white PNG/JPEG at 200×200 px for best thermal print quality."
              />
              <BulletItem
                title="Dark Mode:"
                desc="Tap the moon/sun icon to switch between light and dark themes."
              />
              <BulletItem
                title="Switch to Pro:"
                desc="If you already have active Pro validity, swipe the 'Swipe to Pro Mode' bar in Settings to switch instantly. Otherwise tap 'Billing' to start a 7-day free trial or buy a Pro plan."
              />
              <BulletItem
                title="Download Report:"
                desc="Tap 'Download Report' in the Transactions header to export a summary."
              />
            </GuideSection>

            <GuideSection id="n-saathi" icon={Sparkles} title="Saathi AI" openSection={openSection} toggle={toggle}>
              <BulletItem title="What it is:" desc="Saathi is your in-app AI shop assistant. Tap the floating Saathi icon (bottom-right) to ask about your store data — sales, stock, expenses, settings — by typing or speaking." />
              <BulletItem title="Mobile only:" desc="On the Basic plan, Saathi is available on the Mobile app only." />
              <BulletItem title="Free trial limit:" desc="Basic plan gets 5 lifetime free Saathi messages. Once used, upgrade to Pro for 25/day or Ultimate for 50/day." />
              <BulletItem title="Voice input:" desc="Tap the mic to speak your question in 11 Indian languages + English. Saathi auto-transcribes and replies in the same language." />
              <BulletItem title="What you can ask:" desc="Today's revenue · How many bills today · Top 3 products · Low stock items · Monthly expenses · Printer / GST / invoice settings." />
              <BulletItem title="Drag &amp; drop:" desc="Long-press the Saathi icon and drag it anywhere on screen. Drop on the X circle (bottom) to disable. Re-enable from Settings → Saathi AI toggle." />
            </GuideSection>

            <GuideSection id="n-referral" icon={Gift} title="Refer &amp; Earn" openSection={openSection} toggle={toggle}>
              <BulletItem title="Where to find it:" desc="Open Settings (last tab) → scroll below Reward Points → Refer & Earn section." />
              <BulletItem title="Your Referral Code:" desc="Every account gets a unique 8-character referral code. Tap the copy icon to copy it, or tap 'Share Referral Link' to send it via WhatsApp or any other app." />
              <BulletItem title="How it works:" desc="Share your referral link with friends or family. When they sign up using your link, their progress is tracked automatically." />
              <BulletItem title="3 steps your friend must complete:" desc="1. Sign up via your referral link  →  2. Add at least one product  →  3. Record at least one sale." />
              <BulletItem title="Your reward:" desc="You earn 500 reward points as soon as your friend completes all 3 steps. Points are credited automatically the next time you open the app." />
              <BulletItem title="Track progress:" desc="The Refer & Earn section shows each person you referred with a live step-by-step status — Signed Up, Added Product, Made a Sale — and marks completed referrals with '+500 pts earned'." />
              <BulletItem title="In Reward History:" desc="The 500 points appear in your Reward History as 'Referral bonus — [friend's name] completed all steps!'." />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Points are awarded only once per referral, and only when all 3 steps are done. A friend can only be referred once.
              </p>
            </GuideSection>

            <GuideSection
              id="n-rewards"
              icon={Star}
              title="Reward Points"
              openSection={openSection}
              toggle={toggle}
            >
              <p className="font-medium text-slate-900 dark:text-white">
                Earn points by using the app:
              </p>
              <PointsTable
                rows={[
                  ["Refer a friend (completes setup)", "+500"], // UPDATED
                  ["Add product via barcode", "+5"],
                  ["Add product manually", "+1"],
                  ["Record a sale", "+1"],
                  ["Record & share", "+2"],
                ]}
              />
              <p className="font-medium text-slate-900 dark:text-white mt-2">
                Redeem points for Pro:
              </p>
              <PlansTable
                rows={[
                  ["7 Days Pro", "2500", "7 Days"],
                  ["15 Days Pro", "5000", "15 Days"],
                  ["30 Days Pro", "10000", "30 Days"],
                  ["365 Days Pro", "120000", "365 Days"],
                ]}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Points are non-transferable and have no monetary value. They can
                only be redeemed within the app. You can also start a 7-day
                free trial or buy a Pro plan from Settings → Billing.
              </p>
            </GuideSection>

            <GuideSection
              id="n-install"
              icon={Smartphone}
              title="Install the App"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Google Play Store:"
                desc="Search 'DailyHisaab' on Google Play Store and tap Install."
              />
              <BulletItem
                title="Other Stores:"
                desc="Also available on Xiaomi GetApps, Samsung Galaxy Store, Vivo V-AppStore and Indus AppStore."
              />
              <BulletItem
                title="Updates:"
                desc="Keep the app updated from your store to get the latest features and bug fixes."
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                DailyHisaab is a native Android app — installs like any other
                app on your phone. Works offline for viewing cached data.
              </p>
            </GuideSection>

            <GuideSection
              id="n-safety"
              icon={Shield}
              title="Data Safety"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Cloud Storage:"
                desc="Your data lives on our secure, encrypted cloud — always backed up and never lost."
              />
              <BulletItem
                title="Encryption:"
                desc="All connections use HTTPS encryption."
              />
              <BulletItem
                title="No Passwords:"
                desc="We never store your Google password."
              />
              <BulletItem
                title="Camera:"
                desc="Used only for barcode scanning — no images are stored or sent to any server."
              />
              <BulletItem
                title="Bluetooth:"
                desc="Used only for printing — no data is stored or transmitted beyond the printer command."
              />
              <BulletItem
                title="Revoke Access:"
                desc="Remove DailyHisaab's access anytime from your Google Account Permissions."
              />
            </GuideSection>
          </div>
        )}
        {activeTab === "pro" && (
          <div className="space-y-3">
            <GuideSection
              id="p-start"
              icon={Sparkles}
              title="Getting Started with Pro"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="7-Day Free Trial:"
                desc="Brand-new accounts get a one-time 7-day Pro trial. Open Settings → Billing → 'Start 7-Day Free Trial'. No charge during trial."
              />
              <BulletItem
                title="Activate Pro:"
                desc="Open Settings → Billing and pick a plan — 7, 15, 30 or 365 days. Pay securely via Razorpay (UPI / card / netbanking). Pro unlocks instantly."
              />
              <BulletItem
                title="Auto-Renewal:"
                desc="Pro plans renew automatically using your saved Razorpay mandate. You'll see the next charge date in Settings → Billing. Cancel anytime — Pro stays active until the period ends."
              />
              <BulletItem
                title="Redeem Points (alternative):"
                desc="If you have enough reward points, you can redeem them for Pro instead of paying. See the Reward Points section for the points table."
              />
              <BulletItem
                title="When Pro Expires:"
                desc="Your account drops back to Normal Mode automatically. All data stays safe in the cloud — re-activate Pro anytime to use Pro features again."
              />
            </GuideSection>

            <GuideSection
              id="p-dashboard"
              icon={BarChart3}
              title="Dashboard & Analytics"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Today's Sales:"
                desc="See your total sales for the current day at a glance. Tap the card to instantly view all of today's individual sale transactions in a quick-view sheet."
              />
              <BulletItem
                title="Monthly Sales:"
                desc="Total sales for the current calendar month."
              />
              <BulletItem
                title="Total Expenses:"
                desc="All recorded expenses including auto-tracked stock purchase expenses."
              />
              <BulletItem
                title="Weekly Summary Chart:"
                desc="Bar chart showing sales and expenses side-by-side for the past 7 days."
              />
              <BulletItem
                title="Recent Transactions:"
                desc="The latest transactions shown directly on the Dashboard for quick reference."
              />
            </GuideSection>

            <GuideSection
              id="p-products"
              icon={Package}
              title="Products"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="All Normal Features:"
                desc="Everything from Normal mode plus additional fields for full inventory management."
              />
              <BulletItem
                title="Cost Price:"
                desc="Track what you paid for each product to calculate profit margins."
              />
              <BulletItem
                title="Stock Quantity:"
                desc="Track how many units you have in stock. Auto-decrements on sales."
              />
              <BulletItem
                title="Low Stock Threshold:"
                desc="Set a minimum level — get alerted when stock drops below it."
              />
              <BulletItem
                title="HSN Code:"
                desc="Add Harmonized System code for GST compliance."
              />
              <BulletItem
                title="Per-Product GST Rate:"
                desc="Set individual GST rates for each product."
              />
              <BulletItem
                title="Barcode:"
                desc="Assign or scan barcodes for quick product lookup during billing."
              />
              <BulletItem
                title="Print Barcode Labels:"
                desc="Tap 'Print Barcodes' to preview all barcode labels. Set a global quantity or adjust per-product quantity (including 0 to skip a product). Print all via Bluetooth thermal printer."
              />
            </GuideSection>

            <GuideSection
              id="p-billing"
              icon={Home}
              title="Pro Billing"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Tap to Select:"
                desc="Tap any product on the Products page to add it to your billing cart."
              />
              <BulletItem
                title="Scan to Bill:"
                desc="Tap the scan icon on the Products page and point your phone camera at a product barcode — the matching product is added to the cart instantly. Great for fast checkout."
              />
              <BulletItem
                title="Quantity Controls:"
                desc="Use + and − buttons in the billing bar or on individual product cards to adjust quantities."
              />
              <BulletItem
                title="Sale or Expense:"
                desc="Choose Sale or Expense before recording."
              />
              <BulletItem
                title="Discount:"
                desc="Apply a bill-level discount before finalising."
              />
              <BulletItem
                title="Auto Stock Update:"
                desc="Stock quantity is automatically reduced for sales and increased for expenses."
              />
              <BulletItem
                title="Print Receipt:"
                desc="After recording a sale, tap 'Print Receipt' on the success screen to print directly to a Bluetooth thermal printer."
              />
            </GuideSection>

            <GuideSection
              id="p-stock-expense"
              icon={IndianRupee}
              title="Auto Stock Expense Tracking"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="How it Works:"
                desc="When you add or update a product with stock quantity and cost price, DailyHisaab automatically creates an expense transaction to reflect your purchase cost."
              />
              <BulletItem
                title="Calculation:"
                desc="Expense = Stock Quantity × Cost Price. This flows directly into Total Expenses on the Dashboard."
              />
              <BulletItem
                title="Automatic:"
                desc="No manual entry needed — stock expenses are tracked as soon as you save a product with stock and cost details."
              />
            </GuideSection>

            <GuideSection
              id="p-alerts"
              icon={Bell}
              title="Stock Alerts"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Low Stock Alert:"
                desc="When a product's stock falls below the threshold you set, you get an in-app notification."
              />
              <BulletItem
                title="Out of Stock:"
                desc="When stock reaches zero, you get an urgent alert so you can reorder immediately."
              />
              <BulletItem
                title="Bell Icon:"
                desc="A bell icon in the header shows a red badge count when you have unread alerts."
              />
              <BulletItem
                title="Mark as Read:"
                desc="Tap on alerts to mark them as read and clear the badge."
              />
              <BulletItem
                title="Dismiss:"
                desc="Dismiss alerts you don't need to act on — dismissed alerts won't reappear."
              />
            </GuideSection>

            <GuideSection
              id="p-transactions"
              icon={IndianRupee}
              title="Transactions"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Today's Sales:"
                desc="Tap the 'Today's Sales' card at the top to view all of today's sale transactions in a quick-view sheet."
              />
              <BulletItem
                title="Filter by Type & Date:"
                desc="Use the dropdowns to filter by All/Sale/Expense and by All Time/Today/Week/Month/Year."
              />
              <BulletItem
                title="Bill Number:"
                desc="Each transaction shows a short 8-character bill ID matching what appears on the printed receipt and shared invoice."
              />
              <BulletItem
                title="Share Bill:"
                desc="Tap 'Share bill' to generate the invoice and share via WhatsApp, link, or PDF."
              />
              <BulletItem
                title="Convert to GST:"
                desc="Tap 'Convert to GST' on any non-GST sale to generate a GST-compliant invoice with full tax breakdown."
              />
              <BulletItem
                title="Download Report:"
                desc="Tap 'Download Report' in the header to export a transaction summary."
              />
              <BulletItem
                title="Delete:"
                desc="Remove any transaction using the trash icon. This also updates stock if applicable."
              />
            </GuideSection>

            <GuideSection
              id="p-invoices"
              icon={IndianRupee}
              title="Invoices & GST"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Generate Invoice:"
                desc="After recording a sale, tap 'Share bill' in Transactions to open the invoice."
              />
              <BulletItem
                title="Bill Number:"
                desc="Each invoice shows a unique 8-character bill number (e.g. KSHLGCL1) derived from the order ID — consistent across the app, receipt, and PDF."
              />
              <BulletItem
                title="Invoice Details:"
                desc="Includes your business name, logo, address, itemized list with quantities, rates, totals, and GST breakdown."
              />
              <BulletItem
                title="Public Invoice Link:"
                desc="Share a public URL so customers can view their bill on any device without logging in."
              />
              <BulletItem
                title="Download PDF:"
                desc="Save the invoice as a PDF directly to your device."
              />
              <BulletItem
                title="GST Bills:"
                desc="Convert any regular sale into a GST-compliant invoice. Once converted, it shows the full HSN, CGST, SGST breakdown."
              />
              <BulletItem
                title="Non-GST Orders:"
                desc="Shown as ORDER # on the invoice. Converting to GST creates a proper INV- numbered invoice."
              />
            </GuideSection>

            <GuideSection
              id="p-bt"
              icon={Bluetooth}
              title="Bluetooth & USB Printing"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Two Printer Slots:"
                desc="Pro supports TWO printers at the same time — a Receipt printer (ESC/POS, 58mm or 80mm) for bills and a Label printer (TSPL / ZPL / ESC/POS) for product barcode stickers. Pair each in Settings → Printer Setup."
              />
              <BulletItem
                title="Bluetooth or USB (OTG):"
                desc="Connect printers wirelessly via Bluetooth, or wired via a USB OTG cable from your phone. Both work for receipt and label printers — pick whichever your printer supports."
              />
              <BulletItem
                title="Print Receipts:"
                desc="After a sale, tap 'Print Receipt' on the success screen. The receipt prints on your paired Receipt printer."
              />
              <BulletItem
                title="Reprint from Transactions:"
                desc="Open any past transaction, tap 'Share bill' → 'Print Receipt' to reprint from the invoice preview."
              />
              <BulletItem
                title="Print Barcode Labels:"
                desc="Go to Products → 'Print Barcodes'. Set global or per-product quantity (0 to skip a product), then tap 'Print via BT'. Labels print on your paired Label printer."
              />
              <BulletItem
                title="Label Format:"
                desc="Pick TSPL, ZPL or ESC/POS in Printer Setup to match your label printer. Prints 1D (Code-128) barcode labels."
              />
              <BulletItem
                title="Connecting a Printer:"
                desc="In Printer Setup, tap 'Connect' on the slot you want — the app shows a live scan list with signal strength. Pick your printer once and it's remembered for next time."
              />
              <BulletItem
                title="Supported Receipt Printers:"
                desc="Any ESC/POS Bluetooth thermal printer (58mm or 80mm paper)."
              />
              <BulletItem
                title="Logo on Receipt:"
                desc="Upload a black-and-white PNG/JPEG logo (200×200 px) in Settings → Business Profile. It prints at the top of every receipt."
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Bluetooth + USB OTG printing works on the DailyHisaab Android
                app. For Bluetooth, pair the printer once in your phone's
                Bluetooth settings first. For USB, plug your printer into the
                phone using a USB OTG cable and grant permission when prompted.
              </p>
            </GuideSection>

            <GuideSection
              id="p-gst"
              icon={Settings}
              title="GST Settings"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Enable GST:"
                desc="Toggle GST on/off in Settings → GST Configuration."
              />
              <BulletItem
                title="Inclusive/Exclusive:"
                desc="Choose whether your listed prices include or exclude GST."
              />
              <BulletItem
                title="GSTIN:"
                desc="Enter your GST Identification Number — it appears on all GST invoices."
              />
              <BulletItem
                title="CIN & FSSAI:"
                desc="Add your Company Identification Number and FSSAI licence number for regulated businesses."
              />
              <BulletItem
                title="Default GST Rate:"
                desc="Set a default GST percentage that applies to all products unless overridden per product."
              />
              <BulletItem
                title="Audit-Safe INV Numbers:"
                desc="GST invoices use sequential INV-NNNN serials per CGST Rule 46(b). Once issued, the serial is preserved — even if you cancel the bill (see below)."
              />
              <BulletItem
                title="Cancel, Don't Delete (GST bills):"
                desc="On Mobile, tap a GST bill in Transactions and choose Cancel. The bill is marked CANCELLED — stock + reward points revert, but the INV serial is preserved. Customer's public-link receipt shows a 'Cancelled' banner. Cancelled bills appear under 'Cancelled' in your GST Filing report. Non-GST cash memos still hard-delete."
              />
            </GuideSection>

            <GuideSection
              id="p-gst-filing"
              icon={Receipt}
              title="GST Filing (NEW)"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="One-Click GSTR-1 + 3B:"
                desc="Settings → GST Filing → pick a month or quarter → Download Excel + PDF. Generates GSTR-1 (B2CS, HSN Summary, Nil-Rated, Documents Issued) and GSTR-3B summary in CA-ready format."
              />
              <BulletItem
                title="Hand to Your CA:"
                desc="The Excel column names match the GSTN portal offline-utility template. Your CA can copy values directly into the portal — no rekeying."
              />
              <BulletItem
                title="Place of Supply:"
                desc="Auto-derived from your GSTIN's first 2 digits. Make sure your GSTIN is correctly set in Business Profile (15 chars, e.g. 19BDXPM8993D1ZI for West Bengal)."
              />
              <BulletItem
                title="What's NOT covered (file manually):"
                desc="v1 covers B2C intra-state outward supplies only. B2B invoices, B2C Large > ₹2.5L inter-state, Credit/Debit Notes, exports, e-commerce, and GSTR-3B Table 4 ITC must be entered on the GST portal manually. The PDF footer reminds you of this on every page."
              />
              <BulletItem
                title="QRMP Quarterly:"
                desc="Filing under the QRMP scheme? Switch the period picker from Monthly to Quarterly to generate a fiscal-quarter (Apr-Jun, Jul-Sep, Oct-Dec, Jan-Mar) report."
              />
            </GuideSection>

            <GuideSection id="p-saathi" icon={Sparkles} title="Saathi AI" openSection={openSection} toggle={toggle}>
              <BulletItem title="What it is:" desc="Saathi is your in-app AI shop assistant. Tap the floating Saathi icon (bottom-right) to ask about your store data — sales, stock, expenses, settings — by typing or speaking." />
              <BulletItem title="Mobile only on Pro:" desc="On the Pro plan, Saathi is available on the Mobile app only. Ultimate plan unlocks Saathi on Desktop too." />
              <BulletItem title="Daily limit:" desc="Pro: 25 Saathi questions per day. Resets at midnight IST. Upgrade to Ultimate for 50/day." />
              <BulletItem title="Voice input:" desc="Tap the mic to speak in 11 Indian languages + English. Hindi, Bengali, Tamil, Telugu, Kannada, Malayalam, Marathi, Gujarati, Punjabi, Urdu — Saathi replies in the same language." />
              <BulletItem title="What you can ask:" desc="Today's revenue · How many bills today · Top 3 products · Low stock items · Monthly expenses · Printer / GST / invoice settings." />
              <BulletItem title="Drag &amp; drop:" desc="Long-press the Saathi icon and drag it anywhere on screen. Drop on the X circle to disable. Re-enable from Settings → Saathi AI toggle." />
            </GuideSection>

            <GuideSection id="p-referral" icon={Gift} title="Refer &amp; Earn" openSection={openSection} toggle={toggle}>
              <BulletItem title="Where to find it:" desc="Open Settings (last tab) → scroll below Reward Points → Refer & Earn section." />
              <BulletItem title="Your Referral Code:" desc="Every account gets a unique 8-character referral code. Tap the copy icon to copy it, or tap 'Share Referral Link' to send it via WhatsApp or any other app." />
              <BulletItem title="How it works:" desc="Share your referral link with friends or family. When they sign up using your link, their progress is tracked automatically." />
              <BulletItem title="3 steps your friend must complete:" desc="1. Sign up via your referral link  →  2. Add at least one product  →  3. Record at least one sale." />
              <BulletItem title="Your reward:" desc="You earn 500 reward points as soon as your friend completes all 3 steps. Points are credited automatically the next time you open the app." />
              <BulletItem title="Track progress:" desc="The Refer & Earn section shows each person you referred with a live step-by-step status — Signed Up, Added Product, Made a Sale — and marks completed referrals with '+500 pts earned'." />
              <BulletItem title="In Reward History:" desc="The 500 points appear in your Reward History as 'Referral bonus — [friend's name] completed all steps!'." />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Points are awarded only once per referral, and only when all 3 steps are done. A friend can only be referred once.
              </p>
            </GuideSection>

            <GuideSection
              id="p-rewards"
              icon={Star}
              title="Reward Points"
              openSection={openSection}
              toggle={toggle}
            >
              <p className="font-medium text-slate-900 dark:text-white">
                Earn points by using the app:
              </p>
              <PointsTable
                rows={[
                  ["Refer a friend (completes setup)", "+500"], // UPDATED
                  ["Add product via barcode", "+5"],
                  ["Add product manually", "+1"],
                  ["Record a sale", "+1"],
                  ["Record & share", "+2"],
                ]}
              />
              <p className="font-medium text-slate-900 dark:text-white mt-2">
                Redeem points for Pro plans:
              </p>
              <PlansTable
                rows={[
                  ["7 Days Pro", "2500", "7 Days"],
                  ["15 Days Pro", "5000", "15 Days"],
                  ["30 Days Pro", "10000", "30 Days"],
                  ["365 Days Pro", "120000", "365 Days"],
                ]}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Points are non-transferable and have no monetary value. You can
                also pay via Razorpay (UPI / card / netbanking) from
                Settings → Billing.
              </p>
            </GuideSection>

            <GuideSection
              id="p-safety"
              icon={Shield}
              title="Data Safety"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Cloud Storage:"
                desc="Your data lives on our secure, encrypted cloud."
              />
              <BulletItem
                title="Encryption:"
                desc="All connections use HTTPS encryption."
              />
              <BulletItem
                title="No Passwords:"
                desc="We never store your Google password."
              />
              <BulletItem
                title="Camera:"
                desc="Used only for barcode scanning — no images are stored or sent to any server."
              />
              <BulletItem
                title="Bluetooth:"
                desc="Used only for printing — no data is stored or transmitted beyond the printer command."
              />
              <BulletItem
                title="Revoke Access:"
                desc="Remove DailyHisaab's access anytime from your Google Account Permissions."
              />
            </GuideSection>
          </div>
        )}
        {activeTab === "ultimate" && (
          <div className="space-y-3">
            <GuideSection
              id="u-start"
              icon={Sparkles}
              title="Welcome to Ultimate"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Mobile = Owner Console:"
                desc="Ultimate mobile is a read-only owner console for monitoring + admin. Full POS billing happens on the DailyHisaab Desktop app — install it on your shop computer to record sales and manage staff."
              />
              <BulletItem
                title="30-Day Free Trial:"
                desc="Brand-new accounts get a one-time 30-day Ultimate trial. Open Settings → Billing → 'Activate 30-Day Free Trial'. No charge, full access to Ultimate + Desktop features."
              />
              <BulletItem
                title="Yearly Plan:"
                desc="Ultimate is a one-time yearly purchase (₹1,999/year). Pay once, valid 365 days. No auto-renewal — you choose when to renew."
              />
              <BulletItem
                title="Renewal Reminders:"
                desc="You'll get email reminders 30 days, 10 days, and on the day of expiry so you never lose access."
              />
              <BulletItem
                title="Premium Dark Theme:"
                desc="Ultimate launches in a premium dark theme by default. Switch to light any time from Settings → Theme."
              />
            </GuideSection>

            <GuideSection
              id="u-dashboard"
              icon={BarChart3}
              title="Owner Dashboard"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Period KPIs:"
                desc="Switch between Today / 7 Days / 30 Days / 1 Year to see earnings, expenses, profit/loss, sale count and inventory value at a glance."
              />
              <BulletItem
                title="Top Selling Products:"
                desc="Live chart of your top 5 products by quantity sold over the selected period — spot fast movers instantly."
              />
              <BulletItem
                title="Low Stock Alert Count:"
                desc="See at a glance how many products are below their low-stock threshold so you can plan reorders."
              />
              <BulletItem
                title="Read-Only View:"
                desc="No record-sale button on mobile — Ultimate mobile is for monitoring. Use Desktop app for billing."
              />
            </GuideSection>

            <GuideSection
              id="u-transactions"
              icon={IndianRupee}
              title="Transactions"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Period + Custom Range:"
                desc="Filter by Today, 7D, 30D, 1Y, or pick a custom date range."
              />
              <BulletItem
                title="Grouped by Bill:"
                desc="Items from the same sale are grouped under one bill total — tap to see the full item breakdown."
              />
              <BulletItem
                title="Search:"
                desc="Search by invoice number or product name."
              />
              <BulletItem
                title="Reprint Last 90 Days:"
                desc="Tap any sale within the last 90 days to reprint the receipt or share the invoice. Older sales are view-only."
              />
              <BulletItem
                title="No Delete:"
                desc="Mobile is view-only. Delete or edit transactions from the Desktop app."
              />
              <BulletItem
                title="Cancelled Invoices:"
                desc="GST bills cancelled on Desktop appear here with a red CANCELLED badge + strike-through. The INV serial is preserved (CGST Rule 46(b)) and the bill counts under 'Cancelled' in your GST Filing report."
              />
            </GuideSection>

            <GuideSection
              id="u-products"
              icon={Package}
              title="Products"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Grid or List View:"
                desc="Toggle between grid and list — your choice is remembered next time."
              />
              <BulletItem
                title="Stock Pills:"
                desc="Green = healthy, Gold = low stock, Red = out of stock. Spot what needs reordering at a glance."
              />
              <BulletItem
                title="Filters:"
                desc="Filter by All / Low Stock / Out of Stock / Healthy. Search by product name."
              />
              <BulletItem
                title="Product Detail Sheet:"
                desc="Tap any product to see sale price, cost price, current stock, HSN, 30-day sales velocity, and last-sold date."
              />
              <BulletItem
                title="Print Barcode Labels:"
                desc="If a product has a barcode assigned, you can print labels via your paired Bluetooth label printer (TSPL / ZPL / ESC/POS). Set quantity per product."
              />
              <BulletItem
                title="Edit on Desktop:"
                desc="Add, edit or restock products on the Desktop app. Mobile shows a 'Restock from Desktop' note when stock is low."
              />
            </GuideSection>

            <GuideSection
              id="u-desktop"
              icon={Monitor}
              title="Manage Desktop Access"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="2 Desktop Seats:"
                desc="Ultimate includes 2 desktop seats — use both yourself, or share one with staff."
              />
              <BulletItem
                title="Generate Invite Code:"
                desc="In Settings → Manage Desktop Access, tap 'Generate Invite Code'. A 6-digit code valid for 10 minutes is created — share it with your staff to log them into a desktop without giving them your password."
              />
              <BulletItem
                title="Active Devices:"
                desc="See every device connected to your account — owner + staff, mobile + desktop. Your current device is tagged 'THIS DEVICE'."
              />
              <BulletItem
                title="Revoke Anytime:"
                desc="Tap revoke on any device — staff is signed out within seconds. Useful when someone leaves or loses a device."
              />
              <BulletItem
                title="Staff Locks (Permissions):"
                desc="Toggle what staff can see + do on Desktop: dashboard, transactions, products, reports, GST Filing, Saathi, store, exports, prices, transaction delete, profile view, and more."
              />
              <BulletItem
                title="GST Filing (locked by default):"
                desc="The new GST Filing page (GSTR-1 + 3B) is HIDDEN from staff by default. Toggle 'Hide GST Filing' OFF only for trusted staff who handle compliance. Sales/cashier staff should never see it."
              />
              <BulletItem
                title="Staff Profile Masking:"
                desc="By default, staff sees masked owner profile (email, phone, ID hidden as ••••). Toggle 'Show Owner Profile' ON to reveal — staff still gets read-only view, can't edit."
              />
            </GuideSection>

            <GuideSection
              id="u-gst-filing"
              icon={Receipt}
              title="GST Filing (NEW)"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="GSTR-1 + GSTR-3B:"
                desc="Settings → GST Filing. Pick a month or quarter. Download Excel + PDF. Auto-aggregates B2CS, HSN Summary, Nil-Rated, Documents Issued, and GSTR-3B summary across all your live GST bills in the period."
              />
              <BulletItem
                title="Audit-Safe Documents Issued:"
                desc="The Documents Issued section shows your INV serial range (From / To), total bills issued, and how many were cancelled. Cancelled GST bills are counted but excluded from the tax sums — that's how CGST Rule 46(b) wants it."
              />
              <BulletItem
                title="Reconciliation Hard-Fail:"
                desc="If there's any drift between line-item taxable values and the rolled-up B2CS/Nil-Rated totals (more than ₹0.05), the report REFUSES to generate and shows an error. Better to fix the source bill than file a misleading return."
              />
              <BulletItem
                title="Place of Supply via GSTIN:"
                desc="Auto-derived from your GSTIN's first 2 digits. If your GSTIN is missing or malformed (not 15 chars), the page shows a setup warning + blocks generation."
              />
              <BulletItem
                title="What's NOT covered (file on portal manually):"
                desc="v1 covers B2C intra-state outward supplies only. B2B invoices, B2C Large > ₹2.5L inter-state, Credit/Debit Notes, exports, e-commerce, GSTR-3B Table 4 ITC. The PDF footer reminds you of this on every page."
              />
              <BulletItem
                title="Available on Mobile + Desktop:"
                desc="Generate from Mobile (file saves to /Documents/) or Desktop (browser blob download). Same aggregator, identical output. Both copies stay in sync."
              />
            </GuideSection>

            <GuideSection
              id="u-saathi"
              icon={Sparkles}
              title="Saathi AI (Owner)"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Available on Mobile + Desktop:"
                desc="Ultimate owners get Saathi AI on BOTH the mobile app and desktop app. Staff devices on Ultimate get mobile Saathi only (controlled by Saathi staff lock)."
              />
              <BulletItem
                title="Highest daily limit:"
                desc="Ultimate owner: 55 Saathi questions per day (50 base + 5 owner bonus). Ultimate staff: 50 per day. Resets at midnight IST."
              />
              <BulletItem
                title="Voice input:"
                desc="Tap the mic to speak in 11 Indian languages + English. Saathi replies in the same language. Desktop shortcut: Ctrl+Shift+A to open, Ctrl+Shift+V to open + start mic."
              />
              <BulletItem
                title="What Saathi can answer:"
                desc="Today's revenue · Bill count · Top 3 products · Low stock items · Monthly expenses · Printer width · GST status · Invoice layout."
              />
              <BulletItem
                title="Drag &amp; drop (mobile):"
                desc="Long-press the Saathi icon and drag anywhere on screen. Drop on X circle to disable. Re-enable from Settings → Saathi AI toggle."
              />
              <BulletItem
                title="Staff control:"
                desc="In Manage Desktop Access → Staff Locks → toggle Saathi off to block staff from using AI assistant on Desktop."
              />
            </GuideSection>

            <GuideSection
              id="u-aikey"
              icon={Sparkles}
              title="AI Provider Key (Image Gen + Magic Fill)"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="One key, both features:"
                desc="A single API key powers AI Product Images AND Magic Fill (auto-fill HSN / GST rate / Category from product name) on the Desktop app. No more juggling two providers."
              />
              <BulletItem
                title="Choose Gemini OR OpenAI:"
                desc="Settings → AI Provider Key → pick provider → paste key → Save. Gemini default — generous free tier, billed to your Google account. Choose OpenAI if you prefer DALL·E images."
              />
              <BulletItem
                title="Free quotas first:"
                desc="Without your own key, every Ultimate user gets 5 AI image generations + 20 Magic Fills per day on DailyHisaab managed quota. Add your own key only when you hit the limit."
              />
              <BulletItem
                title="Switch providers:"
                desc="To switch from Gemini to OpenAI (or back), revoke the current key first — picker unlocks → pick the other provider → paste new key. One provider active at a time per account."
              />
              <BulletItem
                title="Where to get keys:"
                desc="Gemini: ai.google.dev (free Google account works). OpenAI: platform.openai.com. Both consoles let you create + revoke keys yourself."
              />
              <BulletItem
                title="Used on Desktop:"
                desc="Set the key from the mobile owner app — it syncs instantly. AI Product Images + Magic Fill buttons appear in the Add Product dialog on the Desktop app."
              />
              <BulletItem
                title="Security:"
                desc="Your key is stored write-only in a private Firestore vault. Never read back to your browser. Only DailyHisaab Cloud Functions decrypt it at the moment of an AI call."
              />
            </GuideSection>

            <GuideSection
              id="u-staff"
              icon={Activity}
              title="Staff Activity Log"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Audit Trail:"
                desc="See every action your staff takes — sales recorded, expenses, products added/edited/deleted, transactions deleted, settings changed, login/logout."
              />
              <BulletItem
                title="Filter by Staff:"
                desc="Pick a staff member from the dropdown to see only their actions."
              />
              <BulletItem
                title="Recent 50 Events:"
                desc="Latest 50 actions shown. Older events kept for 6 months, then auto-pruned."
              />
              <BulletItem
                title="Owner Actions Hidden:"
                desc="The log shows staff-only activity, so it stays focused on what your team did."
              />
            </GuideSection>

            <GuideSection
              id="u-store"
              icon={ShoppingBag}
              title="DailyHisaab Store"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Buy Hardware:"
                desc="Browse and buy thermal receipt printers, label printers, barcode scanners and cash boxes — straight from the app."
              />
              <BulletItem
                title="Secure Payment:"
                desc="Pay securely via our payment gateway (UPI / card / netbanking). Order confirmation goes to your email."
              />
              <BulletItem
                title="Doorstep Delivery:"
                desc="Items shipped to your business address. Track via the email confirmation."
              />
            </GuideSection>

            <GuideSection
              id="u-subscription"
              icon={Crown}
              title="Subscription & Billing"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="View Plan:"
                desc="Settings → Subscription shows your active plan, expiry date, and days remaining."
              />
              <BulletItem
                title="Renew Yearly:"
                desc="When your year is up, renew via Settings → Billing. Same ₹1,999/year, paid via our secure payment gateway."
              />
              <BulletItem
                title="Payment History:"
                desc="See your last 20 payments and invoices in Settings → Billing."
              />
              <BulletItem
                title="No Auto-Renewal:"
                desc="Ultimate yearly is a one-time payment — you control when to renew. Set a reminder for the expiry date."
              />
            </GuideSection>

            <GuideSection
              id="u-settings"
              icon={Settings}
              title="Settings & Profile"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Business Profile:"
                desc="Tap your profile card to edit owner name, business name, address, GSTIN, CIN, FSSAI, logo and contact details. These appear on every invoice."
              />
              <BulletItem
                title="Printer Setup:"
                desc="Pair Bluetooth Receipt + Label printers — same setup as Pro, two slots."
              />
              <BulletItem
                title="Invoice Settings:"
                desc="Customize invoice layout, footer text, terms and QR-code printing."
              />
              <BulletItem
                title="Theme:"
                desc="Switch between dark (default) and light themes."
              />
              <BulletItem
                title="Help & Support:"
                desc="Tap 'Need Help?' to open a support ticket — Ultimate users get priority response."
              />
            </GuideSection>

            <GuideSection
              id="u-safety"
              icon={Shield}
              title="Data Safety"
              openSection={openSection}
              toggle={toggle}
            >
              <BulletItem
                title="Cloud Storage:"
                desc="Your data lives on our secure, encrypted cloud — synced across mobile + every desktop seat."
              />
              <BulletItem
                title="Encryption:"
                desc="All connections use HTTPS encryption."
              />
              <BulletItem
                title="Staff Access Control:"
                desc="Revoke any staff device instantly. Staff cannot see owner profile fields when masking is on."
              />
              <BulletItem
                title="Audit Trail:"
                desc="Every staff action is logged — full accountability for sales, edits and deletes."
              />
              <BulletItem
                title="Bluetooth:"
                desc="Used only for printer commands — nothing stored or transmitted elsewhere. Barcode scanning happens on the Desktop app."
              />
            </GuideSection>
          </div>
        )}

        {/* ── UPDATED SUPPORT CARD ── */}
        <div className="pt-2 pb-4">
          <Link href="/support">
            <a
              className="w-full bg-white dark:bg-slate-900 rounded-3xl p-4 shadow-sm border border-slate-100 dark:border-slate-800 flex items-center gap-3 active:scale-[0.98] transition-transform no-underline cursor-pointer"
              data-testid="guide-support-link"
            >
              <div className="w-10 h-10 rounded-2xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-base text-slate-900 dark:text-white">
                  Still need help?
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                  support@dailyhisaab.com
                </p>
              </div>
              <HelpCircle className="h-4 w-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
            </a>
          </Link>
        </div>
      </div>
    </div>
  );
}